import { useState, useEffect, useCallback } from 'react';
import { img, readdy } from '@/utils/images';
import { Link } from 'react-router-dom';
import { fetchArticles, migrateNewsToSupabase, type NewsArticle } from '@/utils/adminStore';
import ScrollReveal from '@/components/base/ScrollReveal';

const categoryLabels: Record<string, string> = {
  brand: '品牌新闻',
  training: '培训赋能',
  store: '门店动态',
  franchise: '招商资讯',
  health: '健康知识',
};

// ====== Client-side cache for instant load on repeat visits ======
const CACHE_KEY = 'home_news_preview_cache';
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

interface CacheEntry {
  data: NewsArticle[];
  timestamp: number;
}

function getCachedArticles(): NewsArticle[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cache: CacheEntry = JSON.parse(raw);
    if (Date.now() - cache.timestamp > CACHE_TTL) return null;
    if (!Array.isArray(cache.data) || cache.data.length === 0) return null;
    return cache.data;
  } catch {
    return null;
  }
}

function setCachedArticles(articles: NewsArticle[]) {
  try {
    const entry: CacheEntry = { data: articles, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // storage full or disabled, silently ignore
  }
}

export default function NewsPreview() {
  const cached = getCachedArticles();
  const [articles, setArticles] = useState<NewsArticle[]>(cached || []);
  const [isFresh, setIsFresh] = useState(!!cached);

  const loadData = useCallback(async () => {
    try {
      await migrateNewsToSupabase();
      const data = await fetchArticles();
      const top3 = data.slice(0, 3);
      if (top3.length > 0) {
        setArticles(top3);
        setCachedArticles(top3);
        setIsFresh(true);
      }
    } catch {
      // silently fail — homepage shouldn't break for news
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Still show cached content while fetching in background
  if (!isFresh && articles.length === 0) {
    return (
      <section className="relative bg-white py-16 md:py-24 overflow-hidden">
        <div className="px-6 md:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 md:mb-16">
              <div>
                <p className="text-brand-brown text-sm tracking-widest mb-3 font-medium">品牌动态</p>
                <h2 className="text-3xl md:text-4xl font-bold text-brand-deep leading-tight">
                  最新资讯
                </h2>
              </div>
              <Link
                to="/news"
                className="inline-flex items-center gap-2 text-sm text-brand-brown hover:text-brand-dark transition-colors mt-3 md:mt-0 cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-2xl overflow-hidden bg-white border border-brand-light/40">
                  <div className="aspect-[16/9] bg-brand-light/30 animate-pulse" />
                  <div className="p-5 md:p-6 space-y-3">
                    <div className="h-5 w-20 bg-brand-light/40 rounded-full animate-pulse" />
                    <div className="h-5 w-full bg-brand-light/30 rounded animate-pulse" />
                    <div className="h-5 w-3/4 bg-brand-light/30 rounded animate-pulse" />
                    <div className="h-3 w-24 bg-brand-light/20 rounded animate-pulse mt-3" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-white py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]">
        <img
          src={readdy('news-preview-bg-v2')}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal variant="fade-up">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 md:mb-16">
              <div>
                <p className="text-brand-brown text-sm tracking-widest mb-3 font-medium">品牌动态</p>
                <h2 className="text-3xl md:text-4xl font-bold text-brand-deep leading-tight">
                  最新资讯
                </h2>
              </div>
              <Link
                to="/news"
                className="inline-flex items-center gap-2 text-sm text-brand-brown hover:text-brand-dark transition-colors mt-3 md:mt-0 cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {articles.map((item, index) => (
              <ScrollReveal key={item.id} variant="fade-up" delay={index * 100}>
                <Link
                  to={`/news/${item.id}`}
                  className="group cursor-pointer"
                >
                  <div className="rounded-2xl overflow-hidden bg-white border border-brand-light/40 hover:border-brand-cream hover:shadow-lg transition-all duration-300">
                    <div className="overflow-hidden aspect-[16/9]">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 md:p-6">
                      <span className="inline-block text-xs font-medium text-brand-brown bg-brand-light px-3 py-1 rounded-full mb-3">
                        {categoryLabels[item.category] || item.category}
                      </span>
                      <h3 className="font-semibold text-base text-brand-deep mb-2 group-hover:text-brand-brown transition-colors line-clamp-2">
                        {item.title}
                      </h3>
                      <p className="text-gray-400 text-xs md:text-sm leading-relaxed line-clamp-2">{item.summary}</p>
                      <p className="text-gray-300 text-xs mt-3">{item.date}</p>
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>

          {articles.length === 0 && (
            <div className="text-center py-16">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-brand-light text-gray-300">
                <i className="ri-newspaper-line text-xl" />
              </div>
              <p className="text-gray-400 text-sm">暂无资讯内容</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}